import { useCallback, useEffect, useState } from "react";

/**
 * Which entries the rail carries, and in what order.
 * ---------------------------------------------------------------------------
 * The rail is written as markup - every entry has its own badge, its own menu,
 * its own double-click - so this does not rebuild it. It reads what is there
 * (each entry already carries the route it goes to, as `data-val`) and then
 * tells the ones that are listed where to stand and the ones that are not to
 * stand down: `order` and `display`, written onto the elements the rail
 * rendered. The rail is a flex column already, so order is all it takes.
 *
 * An entry nobody has an opinion about is shown, and a new one that appears in
 * a later version joins the end rather than vanishing because an old saved
 * list did not mention it.
 *
 * Hiding is not locking: the page is still there at its own address, and
 * Settings and Logout are not on the list at all - a rail you can hide your
 * way out of is a rail that can strand someone.
 * ---------------------------------------------------------------------------
 */

const KEY = "ui.nav.entries";
const CHANGED = "nav-entries-changed";
/* An entry is an anchor that knows what it is: the route it goes to, or - for
   the one that opens a menu instead of going anywhere - an id of its own. */
const SELECTOR = ".side-menu a[data-val], .side-menu a[data-nav-id]";

/* ...and everything else in the rail, which has no business being reordered
   but must not be left at order 0 either: a flex item with no order set sorts
   BEFORE one with order 1, so Settings and Logout would climb to the top the
   moment anything else was ordered. */
const TAIL = ".side-menu > a:not([data-val]):not([data-nav-id])";

const idOf = (a) => a.getAttribute("data-val") || a.getAttribute("data-nav-id");
const labelNode = (a) => a.querySelector(".menu-label, [data-nav-text]");

/**
 * Is this an entry at all?
 *
 * The rail carries one or two it ships hidden - a page that is written but not
 * turned on yet (Monitor, today). Those are not entries anyone chose to hide,
 * so they are not entries anyone should be offered: noted the first time they
 * are seen, before anything here has touched their style, and left alone from
 * then on.
 */
function shipped(a) {
  if (!a.dataset.navShipped) a.dataset.navShipped = a.style.display === "none" ? "off" : "on";
  return a.dataset.navShipped;
}

/** What the rail is actually carrying, in the order it was written. */
export function readNavEntries() {
  if (typeof document === "undefined") return [];
  return Array.from(document.querySelectorAll(SELECTOR))
    .filter(a => shipped(a) === "on")
    .map(a => ({
      id: idOf(a),
      label: (a.dataset.navLabel || labelNode(a)?.textContent || "").trim()
        || a.getAttribute("title")
        || idOf(a),
    }))
    .filter(e => e.id);
}

function readSaved() {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) || "[]");
    return Array.isArray(raw) ? raw.filter(e => e && e.id) : [];
  } catch (e) { return []; }
}

/**
 * The saved list, reconciled with the rail as it stands: what is saved first,
 * in its order, then anything the rail has gained since.
 */
export function loadNavConfig(entries = readNavEntries()) {
  const saved = readSaved();
  const known = new Map(entries.map(e => [e.id, e]));
  const out = [];
  saved.forEach(s => {
    const here = known.get(s.id);
    if (!here) return;              /* an entry this version no longer has */
    /* `label` is what the rail calls it; `name` is what someone called it
       instead, kept apart so the original is still there to go back to. */
    out.push({ ...here, on: s.on !== false, name: s.name || "" });
    known.delete(s.id);
  });
  known.forEach(e => out.push({ ...e, on: true, name: "" }));
  return out;
}

export function saveNavConfig(list) {
  try {
    localStorage.setItem(KEY, JSON.stringify(list.map(e => ({
      id: e.id,
      on: e.on !== false,
      ...(e.name && e.name.trim() && e.name.trim() !== e.label ? { name: e.name.trim() } : {}),
    }))));
  } catch (e) { /* private window */ }
  applyNavConfig();
  window.dispatchEvent(new Event(CHANGED));
}

export function forgetNavConfig() {
  try { localStorage.removeItem(KEY); } catch (e) { /* private window */ }
  applyNavConfig();
  window.dispatchEvent(new Event(CHANGED));
}

/**
 * Put the rail in the order it was asked for, hide what was unchecked, and
 * call each entry what it was asked to be called.
 *
 * The rail is React's, so this runs after every render of it: a name written
 * onto a label the component owns would otherwise be the first thing the next
 * render undid.
 */
export function applyNavConfig() {
  if (typeof document === "undefined") return;
  const saved = readSaved();
  const at = new Map(saved.map((e, i) => [e.id, { i, on: e.on !== false, name: e.name }]));
  document.querySelectorAll(SELECTOR).forEach((a, index) => {
    if (shipped(a) === "off") return;   /* not turned on yet; not ours to show */
    const asked = at.get(idOf(a));
    /* an entry nobody has said anything about keeps its place in the markup,
       after everything that was ordered */
    a.style.order = String(asked ? asked.i : saved.length + index);
    a.style.display = asked && !asked.on ? "none" : "";

    const label = labelNode(a);
    if (!label) return;
    /* what the markup calls it, remembered the first time, so a name can be
       taken back off again */
    if (!a.dataset.navLabel) a.dataset.navLabel = label.textContent.trim();
    const call = (asked && asked.name) || a.dataset.navLabel;
    if (label.textContent !== call) label.textContent = call;
    if (a.getAttribute("title")) a.setAttribute("title", call);
  });

  /* Whatever the rail carries that is not an entry stays at the end, in the
     order the markup put it: Settings, then the way out. */
  document.querySelectorAll(TAIL).forEach((a, i) => {
    a.style.order = String(900 + i);
  });
}

/** The list, kept in step with whatever else is open. */
export function useNavConfig() {
  const [list, setList] = useState(() => loadNavConfig());
  const refresh = useCallback(() => setList(loadNavConfig()), []);
  useEffect(() => {
    refresh();
    window.addEventListener(CHANGED, refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener(CHANGED, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, [refresh]);
  return [list, setList, refresh];
}

export default useNavConfig;
